import { useState, useEffect } from "react";
import {
  Checkbox,
  ListItemText,
  MenuItem,
  ListSubheader,
  Avatar,
  TextField,
  Menu,
  Button,
  CircularProgress,
  Box,
  Grid,
  Badge,
} from "@mui/material";
import { countryApi } from "../../api/coursesApi";

export default function CountrySelect({ onChange, onApply }) {
  const initialCountry = Number(localStorage.getItem("targetCountryId"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const open = Boolean(anchorEl);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const res = await countryApi();
      if (res.data.success) {
        const mapped = res.data.data.map((c) => {
          let isoCode = "";
          if (c.locale_code) {
            const parts = c.locale_code.split("-");
            isoCode = parts.length > 1 ? parts[1] : parts[0];
          }

          return {
            id: c.id,
            name: c.country_name,
            code: isoCode,
            flag: isoCode
              ? `https://flagcdn.com/w20/${isoCode.toLowerCase()}.png`
              : null,
          };
        });

        setCountries(mapped);

        // ✅ Preselect initial country here
        if (initialCountry && mapped.some((c) => c.id === initialCountry)) {
          setSelected([initialCountry]);
          onChange?.([initialCountry]);
        }
      }
    } catch (err) {
      console.error("Error fetching countries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const updated = prev.includes(id)
        ? prev.filter((c) => c !== id)
        : [...prev, id];

      onChange?.(updated);

      return updated;
    });
  };

  const handleReset = () => {
    setSearch("");
    setSelected([]);
    onChange?.([]);
    setResetting(true);
  };

  useEffect(() => {
    if (resetting) {
      onApply?.();
      setResetting(false);
    }
  }, [resetting]);

  const filteredCountries = countries.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Dropdown Trigger */}
      <Badge
        badgeContent={selected.length}
        color="error"
        overlap="circular"
        sx={{
          "& .MuiBadge-badge": {
            top: 1,
            right: 3,
            height: "20px",
            minWidth: "18px",
            fontSize: "11px",
            border: "2px solid red",
          },
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleClick}
          className="font-inter"
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            borderRadius: 2,
            width: "120px",
            height: "34px",
            color: "#572575",
            borderColor: "#572575",
            justifyContent: "space-between",
            textTransform: "none",
            "&:hover": {
              borderColor: "#7a3b9a",
              color: "#7a3b9a",
            },
          }}
        >
          Countries
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.5922 13.0893C10.4359 13.2455 10.224 13.3333 10.0031 13.3333C9.78208 13.3333 9.57016 13.2455 9.41389 13.0893L4.69972 8.37512C4.62013 8.29825 4.55664 8.2063 4.51297 8.10463C4.46929 8.00296 4.44631 7.89361 4.44534 7.78296C4.44438 7.67231 4.46547 7.56258 4.50737 7.46016C4.54927 7.35775 4.61115 7.2647 4.68939 7.18646C4.76763 7.10822 4.86068 7.04634 4.96309 7.00444C5.0655 6.96254 5.17524 6.94145 5.28589 6.94241C5.39654 6.94338 5.50589 6.96636 5.60756 7.01004C5.70923 7.05371 5.80118 7.1172 5.87805 7.19679L10.0031 11.3218L14.1281 7.19679C14.2852 7.04499 14.4957 6.961 14.7142 6.96289C14.9327 6.96479 15.1417 7.05243 15.2962 7.20694C15.4507 7.36145 15.5384 7.57046 15.5403 7.78896C15.5422 8.00745 15.4582 8.21795 15.3064 8.37512L10.5922 13.0893Z"
              fill="#572575"
            />
          </svg>
        </Button>
      </Badge>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        disableAutoFocus
        PaperProps={{
          style: {
            minWidth: 320,
            maxHeight: 340,
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Search + List */}
        <Box sx={{ overflowY: "auto", maxHeight: 280 }}>
          <ListSubheader
            sx={{ background: "#fff", position: "sticky", top: 0, zIndex: 1 }}
          >
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    borderRadius: "10px",
                    background: "#fff",
                    height: 36,
                  },
                }}
              />
            </Box>
          </ListSubheader>

          {loading && (
            <MenuItem>
              <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
            </MenuItem>
          )}

          {!loading &&
            filteredCountries.map((country) => (
              <MenuItem
                key={country.id}
                onClick={() => toggleSelect(country.id)}
              >
                <Avatar
                  src={country.flag}
                  alt={country.name}
                  sx={{ width: 24, height: 24, mr: 1 }}
                />
                <ListItemText primary={country.name} />
                <Checkbox checked={selected.includes(country.id)} />
              </MenuItem>
            ))}
        </Box>

        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="center"
          p={1}
        >
          <Grid item xs={6}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleReset}
              sx={{
                borderRadius: "8px",
                color: "#572575",
                borderColor: "#572575",
                fontWeight: 600,
                "&:hover": { borderColor: "#7a3b9a", color: "#7a3b9a" },
              }}
            >
              Reset
            </Button>
          </Grid>

          <Grid item xs={6}>
            <Button
              fullWidth
              onClick={() => {
                onApply?.();
                handleClose();
              }}
              sx={{
                backgroundColor: "#572575",
                borderRadius: "8px",
                color: "white",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#7a3b9a" },
              }}
            >
              {loading ? "Loading..." : "Apply"}
            </Button>
          </Grid>
        </Grid>
      </Menu>
    </>
  );
}
